import { useEffect, useRef, useState } from "react";
import { Sun, CloudRain, Thermometer, Cloud, CloudSun, Droplets, Sunrise, Sunset, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Pick the mood from the live SKY condition (Apple-weather style): rain / cold / sunny / partly / cloudy. */
function moodOf(w) {
  if (!w) return "cloudy";
  if (w.raining || (w.precipitation_mm ?? 0) > 0.5) return "rain";
  if ((w.temperature_c ?? 20) <= 5) return "cold";
  const clouds = w.clouds_pct ?? 50;
  if (clouds <= 25) return "sunny";
  if (clouds <= 70) return "partly";
  return "cloudy";
}

const PARTICLE_COUNT = { sunny: 0, partly: 0, cloudy: 0, cold: 70, rain: 110, mild: 0 };
const MOOD_ICON = { sunny: Sun, partly: CloudSun, cloudy: Cloud, rain: CloudRain, cold: Thermometer, mild: Cloud };
const MOOD_LABEL = {
  sunny: "Clear sky at the park",
  partly: "Partly cloudy at the park",
  cloudy: "Cloudy at the park",
  rain: "Rainy at the park",
  cold: "Cold at the park",
  mild: "Mild at the park",
};
// Apple-weather skies: rich blue for every daytime condition — grey is gone;
// clouds are drawn as crisp gradient puffs by SkyDecor, never blurred
const SKY = {
  sunny: "linear-gradient(180deg, #2a6fc4 0%, #4a8ed9 45%, #7fb4ec 100%)",
  partly: "linear-gradient(180deg, #3879c6 0%, #5696db 48%, #85b8ee 100%)",
  cloudy: "linear-gradient(180deg, #3879c6 0%, #5696db 48%, #85b8ee 100%)",
  rain: "linear-gradient(180deg, #2c3644 0%, #46536b 55%, #64748b 100%)",
  cold: "linear-gradient(180deg, #33628f 0%, #5584b3 50%, #88aed2 100%)",
  mild: "linear-gradient(180deg, #3879c6 0%, #5696db 48%, #85b8ee 100%)",
};
// crisp cloud banks — solid centres with a short feather, no blur filters
const CLOUD_A = `
  radial-gradient(40% 58% at 20% 62%, rgba(255,255,255,1) 0%, rgba(255,255,255,0.9) 34%, rgba(255,255,255,0) 60%),
  radial-gradient(32% 52% at 44% 40%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 36%, rgba(255,255,255,0) 62%),
  radial-gradient(36% 50% at 68% 58%, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.8) 34%, rgba(255,255,255,0) 60%),
  radial-gradient(26% 44% at 88% 52%, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.7) 36%, rgba(255,255,255,0) 62%)`;
const CLOUD_B = `
  radial-gradient(34% 55% at 30% 55%, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.75) 36%, rgba(255,255,255,0) 62%),
  radial-gradient(30% 48% at 62% 45%, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.7) 38%, rgba(255,255,255,0) 62%)`;

/** the sky's characters: a bright sun for clear/partly days, drifting crisp clouds otherwise */
function SkyDecor({ mode }) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {(mode === "sunny" || mode === "partly" || mode === "mild") && (
        <span
          className="absolute right-3 top-3 h-24 w-24"
          style={{ background: "radial-gradient(circle, rgba(255,248,224,1) 0%, rgba(255,228,150,0.6) 30%, rgba(255,228,150,0) 62%)" }}
          data-testid="weather-sky-sun"
        />
      )}
      {mode !== "sunny" && (
        <>
          <span
            className={`absolute -top-3 left-3 h-14 w-56 ${mode === "cloudy" ? "opacity-95" : "opacity-75"}`}
            style={{ background: CLOUD_A, animation: "status-cloud-drift 40s linear infinite alternate" }}
            data-testid="weather-sky-cloud-1"
          />
          <span
            className={`absolute top-8 right-1 h-10 w-40 ${mode === "cloudy" ? "opacity-85" : "opacity-55"}`}
            style={{ background: CLOUD_B, animation: "status-cloud-drift 55s linear infinite alternate-reverse" }}
            data-testid="weather-sky-cloud-2"
          />
        </>
      )}
    </div>
  );
}

function Row({ icon: Icon, label, value, testid }) {
  if (value == null) return null;
  return (
    <span className="flex items-center gap-2 rounded-lg bg-[#0a0a0b]/30 px-2.5 py-1.5 text-[13px] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]" data-testid={testid}>
      <Icon size={15} className="shrink-0 text-[#f8b818]" />
      <span className="font-bold text-white/90">{label}</span>
      <b className="ml-auto whitespace-nowrap pl-2 font-extrabold text-white">{value}</b>
    </span>
  );
}

export default function WeatherFx() {
  const canvasRef = useRef(null);
  const [weather, setWeather] = useState(null);
  const [open, setOpen] = useState(false);
  // gentle 3D: the chart leans toward your pointer and springs back on leave
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const mode = moodOf(weather);

  useEffect(() => {
    fetch(`${API}/weather`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setWeather)
      .catch(() => setWeather(null));
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;
    const fit = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    fit();
    window.addEventListener("resize", fit);

    const count = reduced ? 0 : PARTICLE_COUNT[mode] || 16;
    const rnd = (a, b) => a + Math.random() * (b - a);
    const parts = Array.from({ length: count }, () => ({
      x: rnd(0, w),
      y: rnd(0, h),
      v: rnd(0.4, 1.6),
      s: rnd(1, 3),
      d: rnd(0, Math.PI * 2),
    }));

    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      if (mode === "rain") {
        ctx.strokeStyle = "rgba(200,225,255,0.5)";
        ctx.lineWidth = 1.1;
        for (const p of parts) {
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(p.x - 2.2, p.y + 13 + p.v * 4);
          ctx.stroke();
          p.y += 11 + p.v * 6;
          p.x -= 1.6;
          if (p.y > h + 20) {
            p.y = -20;
            p.x = rnd(0, w + 40);
          }
          if (p.x < -10) p.x = w + 10;
        }
      } else if (mode === "cold") {
        ctx.fillStyle = "rgba(255,255,255,0.85)";
        for (const p of parts) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.s * 0.9, 0, Math.PI * 2);
          ctx.fill();
          p.y += 0.5 + p.v * 0.7;
          p.d += 0.012;
          p.x += Math.sin(p.d) * 0.5;
          if (p.y > h + 6) {
            p.y = -6;
            p.x = rnd(0, w);
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    if (count > 0) raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", fit);
    };
  }, [mode]);

  const MoodIcon = MOOD_ICON[mode];
  const time12 = weather?.observed_at
    ? new Date(`2000-01-01T${weather.observed_at}:00`).toLocaleTimeString("en-ZA", { hour: "numeric", minute: "2-digit", hour12: true }).toLowerCase()
    : null;
  const dateStr = new Date().toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
  const t = weather ? Math.round(weather.temperature_c) : null;

  return (
    <>
    <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden" data-testid="weather-fx" data-weather-mode={mode}>
      {mode === "rain" && <div className="absolute inset-0 bg-[#101828]/30" data-testid="weather-fx-rain-tint" />}
      {mode === "cold" && <div className="absolute inset-0 bg-[#1e3a5f]/20" data-testid="weather-fx-cold-tint" />}
      <canvas ref={canvasRef} className="absolute inset-0" data-testid="weather-fx-canvas" />

      {/* full weather panel — desktop, top-right */}
      {weather && (
        <div
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            const px = (e.clientX - r.left) / r.width - 0.5;
            const py = (e.clientY - r.top) / r.height - 0.5;
            setTilt({ x: -py * 7, y: px * 7 });
          }}
          onMouseLeave={() => setTilt({ x: 0, y: 0 })}
          className="pointer-events-auto absolute right-8 top-36 z-20 hidden w-[336px] overflow-hidden rounded-3xl border-2 border-[#f8b818]/40 shadow-[0_18px_0_-5px_rgba(9,9,11,0.9),0_50px_90px_rgba(0,0,0,0.7)] transition-transform duration-200 ease-out will-change-transform lg:block"
          style={{ transform: `perspective(900px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)` }}
          data-testid="weather-details-panel"
        >
          {/* the sky itself — rich blue, bright sun on clear days, drizzling when it rains */}
          <div className="pointer-events-none absolute inset-0" style={{ background: SKY[mode] }} data-testid="weather-sky" />
          {mode === "rain" && <span className="weather-drizzle pointer-events-none absolute inset-0" data-testid="weather-sky-drizzle" />}
          <SkyDecor mode={mode} />
          {/* glass sheen for the 3D look */}
          <span className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/12 via-transparent to-transparent" />
          {/* soft scrim so the location + time stay readable over the clouds */}
          <span className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-[#101828]/60 to-transparent" />
          <div className="relative p-5 text-white backdrop-blur-[2px]">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-base font-extrabold drop-shadow-[0_1px_3px_rgba(9,9,11,0.7)]" data-testid="weather-panel-location">{weather.location}</p>
              <p className="text-sm font-bold text-white/85 drop-shadow-[0_1px_3px_rgba(9,9,11,0.7)]" data-testid="weather-panel-time">
                {time12} · {dateStr}
              </p>
            </div>
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#f8b818] text-[#14120c] shadow-[0_4px_0_#7a5600,0_10px_20px_rgba(248,184,24,0.4)]">
              <MoodIcon size={26} data-testid="weather-panel-mood-icon" />
            </span>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <p className="text-6xl leading-none text-[#f8b818] drop-shadow-[0_2px_0_rgba(122,86,0,0.7)]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }} data-testid="weather-panel-temp">{t}°C</p>
            <span className="rounded-full border border-white/25 bg-white/10 px-3 py-1.5 text-sm font-extrabold text-white" data-testid="weather-panel-desc">{weather.description}</span>
          </div>
          <div className="mt-4 grid gap-2 border-t border-white/15 pt-4">
            <Row icon={Droplets} label="Humidity" value={weather.humidity_pct != null ? `${weather.humidity_pct}%` : null} testid="weather-humidity" />
            <Row icon={Cloud} label="Clouds" value={weather.clouds_pct != null ? `${weather.clouds_pct}%` : null} testid="weather-clouds" />
          </div>
          {(weather.sunrise || weather.sunset) && (
            <div className="mt-4 flex items-center justify-between border-t border-white/15 pt-3 text-sm" data-testid="weather-suntimes">
              {weather.sunrise && (
                <span className="flex items-center gap-1.5 font-bold text-white">
                  <Sunrise size={15} className="text-[#f8b818]" /> <span className="text-white/85">Sunrise</span> <b className="font-extrabold">{weather.sunrise}</b>
                </span>
              )}
              {weather.sunset && (
                <span className="flex items-center gap-1.5 font-bold text-white">
                  <Sunset size={15} className="text-[#f8b818]" /> <span className="text-white/85">Sunset</span> <b className="font-extrabold">{weather.sunset}</b>
                </span>
              )}
            </div>
          )}
          </div>
        </div>
      )}
    </div>

    {/* mobile chip layer — a sibling ABOVE the hero content (z-10) so the button is tappable, below the navbar (z-50) */}
    <div className="pointer-events-none absolute inset-0 z-30 lg:hidden">
      {/* compact mood chip — phones + tablets: BOTTOM-left, clear of the "Cool Runnings" badge and the slide dots */}
      <span className="absolute bottom-16 left-4 flex" data-testid="weather-fx-badge">
        <button
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label="Toggle weather details"
          className="pointer-events-auto flex items-center gap-1.5 rounded-full border border-white/25 bg-black/40 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-widest text-white/85 backdrop-blur-sm transition-colors hover:bg-black/60"
        >
          <MoodIcon size={11} className="text-[#f8b818]" />
          {t != null ? `${t}°C · ` : ""}{MOOD_LABEL[mode]}
          <ChevronDown size={11} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </button>
      </span>

      {/* mobile weather details card — expands from the chip, away from the menu */}
      {open && weather && (
        <div
          className="pointer-events-auto absolute bottom-24 left-4 w-64 overflow-hidden rounded-2xl border border-white/15 shadow-[0_10px_30px_rgba(0,0,0,0.4)]"
          data-testid="weather-mobile-panel"
        >
          <div className="pointer-events-none absolute inset-0" style={{ background: SKY[mode] }} data-testid="weather-mobile-sky" />
          {mode === "rain" && <span className="weather-drizzle pointer-events-none absolute inset-0" data-testid="weather-mobile-drizzle" />}
          <SkyDecor mode={mode} />
          <span className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-[#101828]/60 to-transparent" />
          <div className="relative p-4 text-white backdrop-blur-[2px]">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-extrabold drop-shadow-[0_1px_3px_rgba(9,9,11,0.7)]" data-testid="weather-mobile-location">{weather.location}</p>
              <p className="text-xs font-bold text-white/85 drop-shadow-[0_1px_3px_rgba(9,9,11,0.7)]" data-testid="weather-mobile-time">{time12} · {dateStr}</p>
            </div>
            <MoodIcon size={22} className="shrink-0" />
          </div>
          <div className="mt-2 flex items-end gap-2">
            <p className="font-display text-4xl leading-none" data-testid="weather-mobile-temp">{t}°C</p>
            <p className="pb-1 text-sm font-bold text-white/85" data-testid="weather-mobile-desc">{weather.description}</p>
          </div>
          <div className="mt-2 space-y-1 border-t border-white/15 pt-2">
            <Row icon={Droplets} label="Humidity" value={weather.humidity_pct != null ? `${weather.humidity_pct}%` : null} testid="weather-mobile-humidity" />
            <Row icon={Cloud} label="Clouds" value={weather.clouds_pct != null ? `${weather.clouds_pct}%` : null} testid="weather-mobile-clouds" />
          </div>
          {(weather.sunrise || weather.sunset) && (
            <div className="mt-2 flex items-center justify-between border-t border-white/15 pt-2 text-xs" data-testid="weather-mobile-suntimes">
              {weather.sunrise && (
                <span className="flex items-center gap-1 font-bold text-white">
                  <Sunrise size={13} className="text-[#f8b818]" /> <b className="font-extrabold">{weather.sunrise}</b>
                </span>
              )}
              {weather.sunset && (
                <span className="flex items-center gap-1 font-bold text-white">
                  <Sunset size={13} className="text-[#f8b818]" /> <b className="font-extrabold">{weather.sunset}</b>
                </span>
              )}
            </div>
          )}
          </div>
        </div>
      )}
    </div>
    </>
  );
}
